/**
 * Passkey API Service
 * Handles all passkey-related API calls
 */

import axios from 'axios';
import { base64urlToBuffer, bufferToBase64url } from '../utils/webauthn';
import { getAuthHeaders, setCurrentUser } from '../utils/auth';
import { PASSKEY_API_BASE } from '../constants';

/**
 * REGISTRATION FLOW
 */

/**
 * Begin passkey registration (Step 1)
 * @returns {Promise<Object>} - PublicKey credential creation options
 */
export async function beginPasskeyRegistration() {
  const response = await axios.post(
    `${PASSKEY_API_BASE}/register/begin/`,
    {},
    {
      headers: getAuthHeaders(),
      withCredentials: true,
    }
  );

  return response.data.publicKey;
}

/**
 * Create passkey credential using browser WebAuthn API (Step 2)
 * @param {Object} publicKeyOptions - Credential creation options from server
 * @param {string} nickname - Optional nickname for the passkey
 * @returns {Promise<Object>} - Created credential and nickname
 */
export async function createPasskeyCredential(publicKeyOptions, nickname = '') {
  // Convert base64url strings to ArrayBuffers
  const options = {
    ...publicKeyOptions,
    challenge: base64urlToBuffer(publicKeyOptions.challenge),
    user: {
      ...publicKeyOptions.user,
      id: base64urlToBuffer(publicKeyOptions.user.id),
    },
  };

  // Create credential using browser WebAuthn API
  const credential = await navigator.credentials.create({
    publicKey: options,
  });

  return { credential, nickname };
}

/**
 * Complete passkey registration (Step 3)
 * @param {PublicKeyCredential} credential - Credential from browser
 * @param {string} nickname - Passkey nickname
 * @returns {Promise<Object>} - Registration result
 */
export async function completePasskeyRegistration(credential, nickname = '') {
  // Serialize credential for transmission
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      transports: credential.response.getTransports?.() || [],
    },
  };

  const response = await axios.post(
    `${PASSKEY_API_BASE}/register/complete/`,
    {
      credential: credentialJSON,
      name: nickname,
    },
    {
      headers: getAuthHeaders(),
      withCredentials: true,
    }
  );

  return response.data;
}

/**
 * Full registration flow - combines all steps
 * @param {string} nickname - Optional nickname for the passkey
 * @returns {Promise<Object>} - Registered passkey details
 */
export async function registerPasskey(nickname = '') {
  // Step 1: Get options from server
  const options = await beginPasskeyRegistration();

  // Step 2: Create credential
  const { credential } = await createPasskeyCredential(options, nickname);

  // Step 3: Complete registration
  const result = await completePasskeyRegistration(credential, nickname);

  if (!result.success) {
    throw new Error(result.detail || 'Registration failed');
  }

  return result.credential;
}

/**
 * AUTHENTICATION FLOW
 */

/**
 * Begin passkey authentication (Step 1)
 * @returns {Promise<Object>} - PublicKey credential request options
 */
export async function beginPasskeyAuthentication() {
  const response = await axios.post(
    `${PASSKEY_API_BASE}/authenticate/begin/`,
    {},
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    }
  );

  return response.data.publicKey;
}

/**
 * Get credential from user's authenticator (Step 2)
 * @param {Object} publicKeyOptions - Credential request options from server
 * @returns {Promise<PublicKeyCredential>} - Assertion from authenticator
 */
export async function getPasskeyAssertion(publicKeyOptions) {
  // Convert challenge to ArrayBuffer
  const options = {
    ...publicKeyOptions,
    challenge: base64urlToBuffer(publicKeyOptions.challenge),
  };

  // Convert allowCredentials if present
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map(cred => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }));
  }

  // Get credential from authenticator
  const assertion = await navigator.credentials.get({
    publicKey: options,
  });

  return assertion;
}

/**
 * Complete passkey authentication (Step 3)
 * @param {PublicKeyCredential} assertion - Assertion from authenticator
 * @returns {Promise<Object>} - Authentication result with tokens and user
 */
export async function completePasskeyAuthentication(assertion) {
  // Serialize assertion for transmission
  const assertionJSON = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : null,
    },
  };

  const response = await axios.post(
    `${PASSKEY_API_BASE}/authenticate/complete/`,
    {
      credential: assertionJSON,
    },
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    }
  );

  return response.data;
}

/**
 * Full authentication flow - combines all steps
 * @returns {Promise<Object>} - Authentication result with access token, refresh token, and user
 */
export async function authenticateWithPasskey() {
  // Step 1: Get challenge from server
  const options = await beginPasskeyAuthentication();

  // Step 2: Get assertion from authenticator
  const assertion = await getPasskeyAssertion(options);

  // Step 3: Complete authentication
  const result = await completePasskeyAuthentication(assertion);

  if (!result.success) {
    throw new Error(result.detail || 'Authentication failed');
  }

  // Backend sets HttpOnly access/refresh cookies in the response — we just
  // record a UI-only marker so RequireAuth and HeaderNav know who's logged in.
  if (result.user) {
    setCurrentUser(result.user);
  }

  return {
    accessToken: result.access,
    refreshToken: result.refresh,
    user: result.user,
  };
}

/**
 * CREDENTIAL MANAGEMENT
 */

/**
 * List user's registered passkeys
 * @returns {Promise<Array>} - Array of passkey objects
 */
export async function listPasskeys() {
  const response = await axios.get(`${PASSKEY_API_BASE}/`, {
    headers: getAuthHeaders(),
    withCredentials: true,
  });

  return response.data;
}

/**
 * Update passkey nickname
 * @param {string} passkeyId - ID of the passkey to update
 * @param {string} nickname - New nickname
 * @returns {Promise<Object>} - Updated passkey object
 */
export async function updatePasskeyNickname(passkeyId, nickname) {
  const response = await axios.patch(
    `${PASSKEY_API_BASE}/${passkeyId}/`,
    { nickname },
    {
      headers: getAuthHeaders(),
      withCredentials: true,
    }
  );

  return response.data;
}

/**
 * Delete a passkey
 * @param {string} passkeyId - ID of the passkey to delete
 * @returns {Promise<boolean>} - True if deleted successfully
 */
export async function deletePasskey(passkeyId) {
  const response = await axios.delete(`${PASSKEY_API_BASE}/${passkeyId}/`, {
    headers: getAuthHeaders(),
    withCredentials: true,
  });

  return response.status === 200 || response.status === 204;
}
